/**
 * A small, centered, Slack-style date separator inserted into the chat
 * timeline above the first message of each calendar day. Uses our neutral-gray
 * hairline + a centered pill label (not Slack's literal styling).
 *
 * Accessibility: rendered as a semantic `role="separator"` whose `aria-label`
 * is the full date, so screen-reader users get the day context that sighted
 * users read from the pill.
 */
type DayDividerProps = {
  /** Human-friendly label, e.g. "Today" / "Yesterday" / "June 9, 2026". */
  label: string;
  /**
   * Stable calendar-day key (e.g. "2026-06-09") surfaced as `data-day` so
   * tests and tooling can assert which day a divider represents.
   */
  day: string;
};

export const DayDivider = ({ label, day }: DayDividerProps) => (
  <div
    className="my-4 flex items-center gap-3"
    role="separator"
    aria-label={label}
    data-testid="chat-day-divider"
    data-day={day}
  >
    <span className="h-px flex-1 bg-puntt-neutral-gray-5" aria-hidden="true" />
    <span className="shrink-0 rounded-full bg-puntt-neutral-gray-3 px-3 py-0.5 text-xs font-medium text-puntt-neutral-gray-11">
      {label}
    </span>
    <span className="h-px flex-1 bg-puntt-neutral-gray-5" aria-hidden="true" />
  </div>
);
